<template>
  <!-- Barra de configuració (només standalone). Model calcat de la DevToolbar de
       vue-dashboard: selector de CLIENT (perfil amb URL de serveis + bearer, un per
       fitxer config/<client>/client.config.js), selector d'IDIOMA i provar connexió.
       Fixa a baix, fosca (Catppuccin), monospace. -->
  <div class="cfg-toolbar">
    <div class="cfg-tab" :title="$t('settings.title')">
      <span class="cfg-badge">CONFIG</span>
      <span class="cfg-state" :class="{ on: hasToken }">{{ hasToken ? '● ' + $t('settings.tokenDefined') : '○ ' + $t('settings.tokenNotDefined') }}</span>
      <span class="cfg-tab-target">{{ currentProfile ? currentProfile.label : '—' }} · {{ host }}</span>
    </div>

    <div class="cfg-body">
      <!-- Fila 1: client + idioma + provar connexió -->
      <div class="cfg-row">
        <div class="cfg-control">
          <label>{{ $t('settings.profile') }}</label>
          <select v-model="state.profile">
            <option v-for="p in profiles" :key="p.key" :value="p.key">{{ p.label }}</option>
          </select>
        </div>
        <div class="cfg-control">
          <label>{{ $t('settings.language') }}</label>
          <select v-model="state.lang">
            <option v-for="l in langOptions" :key="l" :value="l">{{ LANG_LABELS[l] || l }}</option>
          </select>
        </div>
        <div class="cfg-actions">
          <button class="cfg-btn primary" :disabled="testing || !hasToken" @click="onTest">{{ testing ? '…' : $t('settings.test') }}</button>
          <button class="cfg-btn" @click="showAbm = true">{{ $t('progAbm.open') }}</button>
        </div>
        <div v-if="testResult" class="cfg-result" :class="testResult.ok ? 'ok' : 'err'">{{ testResult.message }}</div>
      </div>

      <!-- Fila 2: URL de serveis (del perfil, només lectura) + Bearer (s'enganxa) -->
      <div class="cfg-row">
        <div class="cfg-control cfg-grow">
          <label>{{ $t('settings.baseUrl') }}</label>
          <input type="text" :value="baseUrl" readonly class="cfg-ro" />
        </div>
        <div class="cfg-control cfg-grow">
          <label>Bearer</label>
          <input type="text" :value="token" readonly class="cfg-ro" :placeholder="$t('settings.tokenEmpty')" />
        </div>
      </div>

      <p class="cfg-tokenhelp">{{ $t('settings.tokenHelp') }}</p>
    </div>

    <ProgramsAdmin v-if="showAbm" @close="showAbm = false" />
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, inject, reactive, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { listForms } from '@vft/api/definitionApi.js';
import { getRuntimeConfig, saveRuntimeConfig } from '@vft/config/runtimeConfig.js';
import { normalizeLang } from '@vft/i18n/messages.js';
// Lazy: CodeMirror + ABM només es carreguen quan s'obre el panell (mai en embed).
const ProgramsAdmin = defineAsyncComponent(() => import('@vft/components/ProgramsAdmin.vue'));

const LANG_LABELS = { ca_es: 'Català', es_es: 'Español', en_gb: 'English' };
// Crear/esborrar un programa canvia el glob → Vite recarrega la pàgina i tancaria el
// panell. Persistim l'estat «obert» a sessionStorage per reobrir-lo (a la llista, ja
// actualitzada) després de la recàrrega.
const ABM_KEY = 'sp-abm-open';
const hasSession = typeof sessionStorage !== 'undefined';
const showAbm = ref(hasSession && sessionStorage.getItem(ABM_KEY) === '1');
watch(showAbm, (v) => {
  if (!hasSession) return;
  if (v) sessionStorage.setItem(ABM_KEY, '1');
  else sessionStorage.removeItem(ABM_KEY);
});

const { t, locale } = useI18n({ useScope: 'global' });
// Perfils injectats des de main.js (glob de config/<client>/client.config.js).
const profiles = inject('clientProfiles', []);

const testing = ref(false);
const testResult = ref(null);

const cfg0 = getRuntimeConfig();
const state = reactive({
  profile: (cfg0.profile && profiles.some((p) => p.key === cfg0.profile))
    ? cfg0.profile
    : (profiles[0] ? profiles[0].key : ''),
  lang: cfg0.lang || 'ca_es',
});

const currentProfile = computed(() => profiles.find((p) => p.key === state.profile) || profiles[0] || null);
const baseUrl = computed(() => currentProfile.value?.baseUrl || '');
const langOptions = computed(() => (currentProfile.value?.langs?.length ? currentProfile.value.langs : ['ca_es', 'es_es', 'en_gb']));
const host = computed(() => { try { return new URL(baseUrl.value).host; } catch { return baseUrl.value || '—'; } });
// El bearer es defineix al fitxer del client (config/<client>/client.config.js), no s'enganxa.
const token = computed(() => currentProfile.value?.token || '');
const hasToken = computed(() => !!token.value);

function persist() {
  saveRuntimeConfig({
    profile: state.profile,
    baseUrl: baseUrl.value,
    token: token.value,
    lang: state.lang,
    authScheme: 'bearer',
  });
}

// En arrencar: aplica el perfil vigent (URL + idioma) i persisteix, perquè les crides
// a l'API surtin cap a la URL del client encara que l'usuari no obri mai la barra.
onMounted(() => {
  if (!currentProfile.value) return;
  if (!langOptions.value.includes(state.lang)) state.lang = langOptions.value[0];
  locale.value = normalizeLang(state.lang);
  persist();
});

// Canviar de client: carrega la seva URL + bearer (del fitxer) i ajusta l'idioma si
// el client no el suporta; després persisteix.
watch(() => state.profile, () => {
  const p = currentProfile.value;
  if (!p) return;
  if (!langOptions.value.includes(state.lang)) state.lang = langOptions.value[0];
  persist();
});
watch(() => state.lang, (v) => { locale.value = normalizeLang(v); persist(); });

// Prova la connexió contra l'endpoint real de formularis (formlist/task).
async function onTest() {
  testing.value = true;
  testResult.value = null;
  persist();
  try {
    const forms = await listForms();
    testResult.value = { ok: true, message: t('settings.testItems', { n: forms.length }) };
  } catch (err) {
    const status = err?.response?.status;
    const msg = err?.response?.statusText || err?.message || 'Error';
    testResult.value = { ok: false, message: `${status ? status + ' ' : ''}${msg}` };
  } finally {
    testing.value = false;
  }
}
</script>

<style scoped>
/* Colocació i estil calcats de vue-dashboard/DevToolbar (Catppuccin). */
.cfg-toolbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1e1e2e;
  color: #cdd6f4;
  z-index: 99999;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}
.cfg-tab {
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #313244;
  color: #cdd6f4;
  padding: 4px 16px;
  font: inherit;
  text-align: left;
}
.cfg-badge {
  background: #89b4fa;
  color: #1e1e2e;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
}
.cfg-state { opacity: 0.7; }
.cfg-state.on { color: #a6e3a1; opacity: 1; }
.cfg-tab-target { opacity: 0.55; }

.cfg-body { display: flex; flex-direction: column; }
.cfg-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 16px 6px;
  border-top: 1px solid #313244;
  flex-wrap: wrap;
}
.cfg-control { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.cfg-grow { flex: 1; min-width: 220px; }
.cfg-control label { opacity: 0.6; white-space: nowrap; }
.cfg-control select,
.cfg-control input[type="text"],
.cfg-control input[type="password"] {
  background: #313244;
  color: #cdd6f4;
  border: 1px solid #45475a;
  border-radius: 4px;
  padding: 2px 8px;
  font-family: inherit;
  font-size: 12px;
  height: 24px;
}
.cfg-grow input { width: 100%; }
.cfg-ro { opacity: 0.55; cursor: default; }
.cfg-control input::placeholder { color: #585b70; }
.cfg-control select:focus,
.cfg-control input:focus { outline: none; border-color: #89b4fa; }
.cfg-eye { background: transparent; border: none; cursor: pointer; font-size: 13px; padding: 0 2px; }
.cfg-actions { display: flex; gap: 6px; }
.cfg-btn {
  background: #313244;
  color: #cdd6f4;
  border: 1px solid #45475a;
  border-radius: 4px;
  padding: 2px 10px;
  height: 24px;
  cursor: pointer;
  font: inherit;
}
.cfg-btn:hover { border-color: #89b4fa; }
.cfg-btn.primary { background: #89b4fa; color: #1e1e2e; font-weight: 600; border-color: #89b4fa; }
.cfg-btn:disabled { opacity: 0.5; cursor: default; }
.cfg-result { font-size: 11px; padding: 2px 8px; border-radius: 3px; }
.cfg-result.ok { color: #a6e3a1; }
.cfg-result.err { color: #f38ba8; }
.cfg-tokenhelp {
  margin: 0;
  padding: 0 16px 8px;
  color: #9399b2;
  line-height: 1.4;
  max-width: 900px;
}
</style>
